import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Phone, Zap, CalendarClock, ClipboardList, Check, Copy } from "lucide-react";
import { toast } from "sonner";
import { LogoMark } from "../components/Logo";
import { getInvoice } from "../lib/api";
import { BUSINESS } from "../lib/constants";

function ZelleRow({ label, value, testid }) {
  const copy = () => {
    navigator.clipboard?.writeText(value).catch(() => {});
    toast.success(`${label} copied`);
  };
  return (
    <button
      data-testid={`invoice-${testid}`}
      onClick={copy}
      className="w-full flex items-center justify-between gap-3 rounded-xl border border-white/15 bg-ink/70 px-4 py-3 text-left hover:border-crystal transition-colors duration-200"
    >
      <div className="min-w-0">
        <div className="font-mono text-[10px] tracking-[0.25em] uppercase text-mist">{label}</div>
        <div className="font-mono text-sm text-white truncate">{value}</div>
      </div>
      <Copy size={14} className="text-mist shrink-0" />
    </button>
  );
}

export default function InvoiceView() {
  const { ref } = useParams();
  const [inv, setInv] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    getInvoice(ref)
      .then(setInv)
      .catch(() => setNotFound(true));
  }, [ref]);

  if (notFound) {
    return (
      <div className="min-h-screen bg-midnight flex items-center justify-center p-6 text-center">
        <div>
          <h1 className="font-display text-3xl text-white">Invoice not found.</h1>
          <p className="text-mist mt-3 text-sm">Check the link, or call <a href={BUSINESS.phoneHref} className="text-crystal">{BUSINESS.phone}</a>.</p>
        </div>
      </div>
    );
  }

  if (!inv) {
    return <div className="min-h-screen bg-midnight" />;
  }

  return (
    <div className="min-h-screen bg-midnight text-white font-body flex items-start sm:items-center justify-center p-4 sm:p-8">
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
        className="w-full max-w-xl rounded-3xl border border-white/15 bg-[#0B0E14] overflow-hidden"
        style={{ boxShadow: "0 60px 140px -40px rgba(0,0,0,1)" }}
        data-testid="invoice-view"
      >
        {/* header */}
        <div className="flex items-center justify-between gap-4 px-7 py-5 border-b border-white/10 bg-ink/60">
          <div className="flex items-center gap-3">
            <LogoMark size={36} />
            <div>
              <div className="font-display text-lg text-white leading-none">Everything Works Services</div>
              <div className="font-mono text-[9px] tracking-[0.3em] uppercase text-mist mt-1.5">Window Cleaning · Naperville, IL</div>
            </div>
          </div>
          <div className="text-right">
            <div className="font-mono text-[10px] text-mist">{inv.ref}</div>
            <span className={`inline-block mt-1 rounded-full border px-3 py-1 text-[9px] tracking-[0.2em] uppercase ${inv.status === "paid" ? "border-emerald-400/40 bg-emerald-400/10 text-emerald-300" : "border-amber-400/40 bg-amber-400/10 text-amber-300"}`} data-testid="invoice-status">
              {inv.status}
            </span>
          </div>
        </div>

        <div className="px-7 py-7 space-y-7">
          {/* appointment */}
          <div className="rounded-2xl border border-crystal/40 bg-crystal/[0.07] px-5 py-4" data-testid="invoice-when">
            <div className="flex items-center gap-2 font-mono text-[10px] tracking-[0.3em] uppercase text-crystal mb-1.5">
              <CalendarClock size={13} /> When I will be there
            </div>
            <div className="font-display text-2xl text-white leading-snug">
              {new Date(inv.date + "T12:00:00").toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric" })} · {inv.time_slot}
            </div>
            <div className="text-mist text-xs mt-1 flex items-center gap-1.5">
              <Phone size={11} /> {inv.address}
            </div>
          </div>

          {/* scope of work */}
          <div data-testid="invoice-scope">
            <div className="flex items-center gap-2 font-mono text-[10px] tracking-[0.3em] uppercase text-crystal mb-3">
              <ClipboardList size={13} /> What I will be doing
            </div>
            <ul className="space-y-2">
              {(inv.scope_lines?.length ? inv.scope_lines : ["Full window cleaning service"]).map((s, i) => (
                <li key={i} className="flex items-start gap-2.5 text-sm text-white/90">
                  <Check size={15} className="text-crystal shrink-0 mt-0.5" /> {s}
                </li>
              ))}
            </ul>
          </div>

          {/* items */}
          <div className="rounded-2xl border border-white/10 overflow-hidden" data-testid="invoice-items">
            {inv.items?.map((it, i) => (
              <div key={i} className="flex items-center justify-between gap-4 px-5 py-3.5 border-b border-white/10 bg-ink/40">
                <span className="text-sm text-white/90">{it.label}</span>
                <span className="font-mono text-sm text-white">${it.amount}</span>
              </div>
            ))}
            <div className="flex items-center justify-between gap-4 px-5 py-4 bg-crystal/[0.07]">
              <span className="font-display text-xl text-white">Total</span>
              <span className="font-display text-3xl text-crystal" data-testid="invoice-total">${inv.total}</span>
            </div>
          </div>

          {/* payment */}
          <div className="space-y-3" data-testid="invoice-zelle">
            <div className="flex items-center gap-2 font-mono text-[10px] tracking-[0.3em] uppercase text-crystal">
              <Zap size={13} /> Pay with Zelle — use {inv.ref} as the memo
            </div>
            <ZelleRow label="Zelle — phone" value={BUSINESS.zellePhone} testid="zelle-phone" />
            <ZelleRow label="Zelle — email" value={BUSINESS.zelleEmail} testid="zelle-email" />
          </div>

          <p className="text-mist text-xs leading-relaxed">
            Questions before I arrive? Call{" "}
            <a href={BUSINESS.phoneHref} className="text-crystal" data-testid="invoice-call-link">{BUSINESS.phone}</a>. See you soon, {inv.customer_name?.split(" ")[0]}.
          </p>
        </div>
      </motion.div>
    </div>
  );
}
